import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Row, Col, Input, Card, CardBody, Form } from "reactstrap";
import { fetchData } from "../../services/apiServices";
import { useNavigate } from "react-router-dom";

const EventsList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSubMarkets, setHasSubMarkets] = useState("all");

  // Fetch categories
  useEffect(() => {
    const getCategories = async () => {
      try {
        const response = await fetchData("api/admin/categories");
        setCategories(response.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getCategories();
  }, []);

  // Fetch events
  const fetchEvents = async (page, search = "") => {
    try {
      setLoading(true);
      let url = `api/event/admin/events?page=${page}&limit=10`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      // Add category filter if selected
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }

      const response = await fetchData(url);
      setEvents(response.events || []);
      setTotalRows(response.pagination.total);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching events:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(currentPage, searchText);
  }, [currentPage, searchText, selectedCategory, hasSubMarkets]);

  // Handle row click to navigate to event detail page
  const handleRowClick = (row) => {
    navigate(`/events/edit/${row._id}`);
  };

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle category filter change
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1); // Reset to first page on new filter
  };

  // Handle sub markets filter change
  const handleSubMarketsChange = (e) => {
    setHasSubMarkets(e.target.value);
    setCurrentPage(1); // Reset to first page on new filter
  };

  // Calculate event status based on dates
  const calculateEventStatus = (event) => {
    const currentTime = new Date();
    const listDate = event.list_date ? new Date(event.list_date) : null;
    const startDate = event.sub_markets[0]?.start_date
      ? new Date(event.sub_markets[0].start_date)
      : null;
    const endDate = event.sub_markets[0]?.end_date
      ? new Date(event.sub_markets[0].end_date)
      : null;

    console.log("Event Dates:", {
      listDate,
      startDate,
      endDate,
      currentTime,
      status: event.status,
    });

    if (listDate && currentTime < listDate) {
      return "Draft";
    } else if (startDate && currentTime < startDate) {
      return "Pre Launch";
    } else if (endDate && currentTime >= startDate && currentTime <= endDate) {
      return "Live";
    } else {
      return "Ended";
    }
  };

  // Calculate time until closing
  const getTimeUntilClosing = (event) => {
    if (!event.sub_markets || event.sub_markets.length === 0) {
      return null;
    }

    // Find the closest end date among all sub markets
    const now = new Date();
    let closestEndDate = null;

    for (const market of event.sub_markets) {
      if (market.end_date) {
        const endDate = new Date(market.end_date);
        if (endDate > now && (!closestEndDate || endDate < closestEndDate)) {
          closestEndDate = endDate;
        }
      }
    }

    if (!closestEndDate) {
      return null;
    }

    // Calculate time difference
    const diffMs = closestEndDate - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );

    if (diffDays > 0) {
      return `closing in ${diffDays} ${diffDays === 1 ? "day" : "days"}`;
    } else if (diffHours > 0) {
      return `closing in ${diffHours} ${diffHours === 1 ? "hour" : "hours"}`;
    } else {
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `closing in ${diffMinutes} ${
        diffMinutes === 1 ? "minute" : "minutes"
      }`;
    }
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Event Image</span>,
      cell: (row) => (
        <div style={{ padding: "5px" }}>
          {row.event_image ? (
            <img
              src={row.event_image}
              alt={row.event_title}
              style={{
                width: "40px",
                height: "40px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />
          ) : (
            <div
              style={{
                width: "60px",
                height: "60px",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
              }}
            >
              No Image
            </div>
          )}
        </div>
      ),
      width: "100px",
    },
    {
      name: <span className="font-weight-bold fs-13">Event Title</span>,
      cell: (row) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            maxWidth: "100%",
          }}
        >
          {row.event_title}
        </div>
      ),
      sortable: true,
      grow: 3,
    },
    {
      name: <span className="font-weight-bold fs-13">Category</span>,
      cell: (row) => {
        const category = categories.find((cat) => cat._id === row.category);
        return (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
            }}
          >
            {category ? category.name : "N/A"}
          </div>
        );
      },
      sortable: true,
      grow: 1,
    },
    {
      name: <span className="font-weight-bold fs-13">Sub Markets</span>,
      cell: (row) => {
        if (!row.has_sub_markets) return "No";
        return (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
            }}
          >
            {row.sub_markets.map((market) => market.name).join(", ")}
          </div>
        );
      },
      grow: 2,
    },
    {
      name: <span className="font-weight-bold fs-13">Status</span>,
      cell: (row) => {
        const status = calculateEventStatus(row);
        let badgeClass = "badge bg-";

        switch (status) {
          case "Draft":
            badgeClass += "warning";
            break;
          case "Pre Launch":
            badgeClass += "info";
            break;
          case "Live":
            badgeClass += "success";
            break;
          case "Ended":
            badgeClass += "secondary";
            break;
          default:
            badgeClass += "danger";
        }

        const closingInfo = status === "Live" ? getTimeUntilClosing(row) : null;

        return (
          <div>
            <span className={badgeClass}>{status}</span>
            {closingInfo && (
              <div className="mt-1">
                <small className="text-muted">{closingInfo}</small>
              </div>
            )}
          </div>
        );
      },
      sortable: true,
      width: "150px",
    },
    {
      name: <span className="font-weight-bold fs-13">Money Pool</span>,
      selector: (row) => `$${row.total_pool_in_usd}`,
      grow: 1,
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <Card>
      <CardBody>
        <Form>
          <Row className="mb-3">
            <Col lg="4">
              <Input
                type="search"
                placeholder="Search events..."
                value={searchText}
                onChange={handleSearchChange}
              />
            </Col>
            <Col lg="3">
              <Input
                type="select"
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
        </Form>

        <DataTable
          columns={columns}
          data={events}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          progressPending={loading}
          responsive
          highlightOnHover
          pointerOnHover
          onRowClicked={handleRowClick}
        />
      </CardBody>
    </Card>
  );
};

export default EventsList;
